// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/ComponentPreview.tsx
// Version: v2.3.0
// Beschreibung: Scrollbar komplett versteckt (iOS-Style)

import { useParams } from "react-router-dom";
import { Component, ErrorInfo, ReactNode, useEffect } from "react";
import { LanguageProvider } from "../lang/index.lang.tsx";

// Komponenten importieren
import AnchorMenu from "../components/AnchorMenu.tsx";
import CareersBanner from "../components/CareersBanner.tsx";
import ContactPerson from "../components/ContactPerson.tsx";
import ContentCard from "../components/ContentCard.tsx";
import Counter from "../components/Counter.tsx";
import FocusCarousel from "../components/FocusCarousel.tsx";
import Footer from "../components/Footer.tsx";
import HeroBanner from "../components/HeroBanner.tsx";
import HeroParallax from "../components/HeroParallax.tsx";
import HeroSlider from "../components/HeroSlider.tsx";
import PartnerGrid from "../components/PartnerGrid.tsx";
import Showcase from "../components/Showcase.tsx";
import ShowcaseSimplified from "../components/ShowcaseSimplified.tsx";

// ===========================================
// ERROR BOUNDARY
// ===========================================
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode; componentName: string }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode; componentName: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Component Preview Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] bg-gray-900 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/50 flex items-center justify-center">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Render-Fehler</h2>
            <p className="text-gray-400 mb-4">
              Die Komponente "{this.props.componentName}" konnte nicht gerendert werden.
            </p>
            <div className="bg-gray-800 rounded-lg p-3 text-left">
              <p className="text-red-400 text-sm font-mono break-all">
                {this.state.error?.message || "Unbekannter Fehler"}
              </p>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===========================================
// MOCK DATA für Komponenten
// ===========================================
const MOCK_DATA: Record<string, any> = {
  HeroSlider: {},
  HeroBanner: {
    title: "Willkommen bei ESTATA",
    subtitle: "Ihr Partner für Immobilien",
    backgroundImage: "/images/hero-bg.jpg",
  },
  HeroParallax: {
    title: "Parallax Hero",
    subtitle: "Mit Tiefeneffekt",
    backgroundImage: "/images/hero-bg.jpg",
  },
  Counter: {
    items: [
      { value: 150, label: "Projekte", suffix: "+" },
      { value: 50, label: "Mitarbeiter" },
      { value: 25, label: "Jahre Erfahrung" },
      { value: 98, label: "Zufriedenheit", suffix: "%" },
    ],
  },
  ContactPerson: {
    title: "Ihre Ansprechpartner",
    subtitle: "Wir sind für Sie da",
    persons: [
      { name: "Max Mustermann", position: "Geschäftsführer", email: "max@estata.com", phone: "+49 123 456789", image: "/images/team/placeholder.jpg" },
      { name: "Erika Musterfrau", position: "Projektleitung", email: "erika@estata.com", phone: "+49 123 456790", image: "/images/team/placeholder.jpg" },
    ],
  },
  ContentCard: {
    cards: [
      { title: "Design & Build", description: "Ganzheitliche Lösungen von der Planung bis zur Umsetzung.", image: "/images/services/design-build.jpg", link: "/services/design-build" },
      { title: "Due Diligence", description: "Technische Prüfung und Bewertung von Immobilien.", image: "/images/services/due-diligence.jpg", link: "/services/due-diligence" },
    ],
  },
  PartnerGrid: {},
  FocusCarousel: {},
  Showcase: {
    title: "Unsere Leistungen",
    subtitle: "Was wir bieten",
  },
  ShowcaseSimplified: {
    title: "Services",
    items: [
      { title: "Planung", description: "Professionelle Bauplanung" },
      { title: "Beratung", description: "Kompetente Beratung" },
    ],
  },
  CareersBanner: {
    title: "Karriere bei ESTATA",
    subtitle: "Werden Sie Teil unseres Teams",
  },
  AnchorMenu: {
    items: [
      { label: "Über uns", anchor: "#about" },
      { label: "Services", anchor: "#services" },
      { label: "Kontakt", anchor: "#contact" },
    ],
  },
  Footer: {},
};

// ===========================================
// KOMPONENTEN REGISTRY
// ===========================================
const COMPONENT_REGISTRY: Record<string, React.ComponentType<any>> = {
  AnchorMenu,
  CareersBanner,
  ContactPerson,
  ContentCard,
  Counter,
  FocusCarousel,
  Footer,
  HeroBanner,
  HeroParallax,
  HeroSlider,
  PartnerGrid,
  Showcase,
  ShowcaseSimplified,
};

// ===========================================
// iOS SCROLLBAR STYLES - Scrollbar komplett verstecken
// ===========================================
const IOS_SCROLLBAR_CSS = `
  /* Scrollbar komplett verstecken - iOS Style */
  ::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  
  /* Firefox */
  * {
    scrollbar-width: none;
  }
  
  html, body {
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  @keyframes clickFeedback {
    0% { transform: scale(0.5); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
  }
`;

// ===========================================
// PREVIEW COMPONENT
// ===========================================
const ComponentPreviewContent = () => {
  const { componentName } = useParams<{ componentName: string }>();

  // Inject iOS Scrollbar Styles + PostMessage Listener
  useEffect(() => {
    // iOS Scrollbar CSS
    const styleEl = document.createElement('style');
    styleEl.id = 'ios-scrollbar-styles';
    styleEl.textContent = IOS_SCROLLBAR_CSS;
    document.head.appendChild(styleEl);

    // PostMessage Listener für Klick-Events vom Admin-Panel
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PREVIEW_CLICK') {
        const { x, y } = event.data;
        
        const element = document.elementFromPoint(x, y);
        if (element) {
          // Visuelles Feedback
          const feedback = document.createElement('div');
          feedback.style.cssText = `
            position: fixed;
            left: ${x - 20}px;
            top: ${y - 20}px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(55, 172, 220, 0.5);
            pointer-events: none;
            z-index: 99999;
            animation: clickFeedback 0.3s ease-out forwards;
          `;
          document.body.appendChild(feedback);
          setTimeout(() => feedback.remove(), 300);
          
          // Klick simulieren
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            clientX: x,
            clientY: y,
            view: window,
          });
          element.dispatchEvent(clickEvent);
          
          console.log('Preview: Click dispatched on', element.tagName, element.className);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      const existingStyle = document.getElementById('ios-scrollbar-styles');
      if (existingStyle) existingStyle.remove();
    };
  }, []);

  if (!componentName) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Kein Komponentenname angegeben</p>
      </div>
    );
  }

  const Component = COMPONENT_REGISTRY[componentName];

  if (!Component) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center text-white max-w-lg">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
            <span className="text-2xl">❓</span>
          </div>
          <h1 className="text-xl font-bold mb-2">Komponente nicht gefunden</h1>
          <p className="text-gray-400 mb-6">"{componentName}" existiert nicht.</p>
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-3">Verfügbare Komponenten:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.keys(COMPONENT_REGISTRY).map((name) => (
                <span key={name} className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-300">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mockProps = MOCK_DATA[componentName] || {};

  return (
    <div className="min-h-screen bg-black">
      {/* Preview Info Bar */}
      <div className="sticky top-0 left-0 right-0 bg-[#37acdc] text-white text-center py-2 text-sm z-50 font-medium shadow-lg">
        🔍 Preview: {componentName}
      </div>
      
      {/* Komponente */}
      <ErrorBoundary componentName={componentName}>
        <Component {...mockProps} />
      </ErrorBoundary>
    </div>
  );
};

// Wrapper mit LanguageProvider
const ComponentPreview = () => {
  return (
    <LanguageProvider>
      <ComponentPreviewContent />
    </LanguageProvider>
  );
};

export default ComponentPreview;
